import { useRef } from 'react'
import Avatar from '../components/Avatar.jsx'
import { fmtDate, fmtMoney, tsToDate } from '../lib/format.js'
import useEnterStagger from '../ds/hooks/useEnterStagger.js'

const rowStyle = {
  display: 'flex', alignItems: 'center', gap: 13, padding: '12px 15px',
  borderBottom: '1px solid var(--surface-row)', cursor: 'pointer', width: '100%',
  background: 'none', border: 'none', textAlign: 'left',
}

function StageGroup({ stage, engagements, usersById, onOpen }) {
  return (
    <div className="cn-eng-group">
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 15px', background: 'var(--color-bg-dark)' }}>
        {stage.dot && <span style={{ width: 8, height: 8, borderRadius: '50%', background: stage.dot, flexShrink: 0 }} />}
        <span className="font-mono" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--color-muted)' }}>{stage.name}</span>
        <span className="font-mono" style={{ fontSize: 11, color: 'var(--color-muted)' }}>{engagements.length}</span>
      </div>
      {engagements.map((e) => {
        const owner = usersById[e.ownerUid]
        const date = fmtDate(tsToDate(e.date))
        return (
          <button key={e.id} type="button" style={rowStyle} onClick={() => onOpen(e.id)}
            onMouseEnter={(ev) => (ev.currentTarget.style.background = 'rgba(200,168,75,.04)')}
            onMouseLeave={(ev) => (ev.currentTarget.style.background = 'none')}>
            <span style={{ flex: '0 0 220px', color: 'var(--color-white)', fontSize: 14 }}>{e.title || '—'}</span>
            <span style={{ flex: 1, color: 'var(--color-muted)', fontSize: 13 }}>{e.org || ''}</span>
            {owner && <Avatar user={owner} size={24} />}
            {date && <span className="font-mono" style={{ flex: '0 0 60px', textAlign: 'right', color: 'var(--color-muted)', fontSize: 11 }}>{date}</span>}
            {typeof e.fee === 'number' && <span className="font-mono" style={{ flex: '0 0 70px', textAlign: 'right', color: 'var(--color-gold)', fontSize: 11 }}>{fmtMoney(e.fee)}</span>}
          </button>
        )
      })}
    </div>
  )
}

export default function EngagementListView({ engagements = [], stages = [], usersById = {}, onOpen }) {
  const stagesById = Object.fromEntries(stages.map((s) => [s.id, s]))
  const byStage = {}
  const unstaged = []
  engagements.forEach((e) => {
    if (stagesById[e.stage]) {
      if (!byStage[e.stage]) byStage[e.stage] = []
      byStage[e.stage].push(e)
    } else {
      unstaged.push(e)
    }
  })

  const orderedStages = [...stages].sort((a, b) => a.order - b.order)
  const groups = orderedStages
    .map((s) => ({ stage: s, list: byStage[s.id] || [] }))
    .filter((g) => g.list.length > 0)

  if (unstaged.length > 0) {
    groups.push({ stage: { id: '__unstaged', name: 'Unstaged', dot: null }, list: unstaged })
  }

  // Stagger the stage-group blocks. Anchored to the stable outer container (always
  // mounted regardless of filtering), so the one-shot played guard survives a
  // filter that momentarily zeroes a stage and remounts it — plays once per load.
  const groupsRef = useRef(null)
  useEnterStagger(groupsRef, { dep: groups.length, selector: ':scope > .cn-eng-group' })

  return (
    <div ref={groupsRef} style={{ margin: '8px 24px 0', background: 'var(--color-panel-dark)', border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      {groups.map((g) => (
        <StageGroup key={g.stage.id} stage={g.stage} engagements={g.list} usersById={usersById} onOpen={onOpen} />
      ))}
      {groups.length === 0 && <div style={{ padding: 20, color: 'var(--color-muted)', fontSize: 13 }}>No engagements yet.</div>}
    </div>
  )
}
